import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Loading from '../../components/Common/Loading';
import { clearError } from '../../store/actions';
import { getUser } from '../../store/users/actions';
import { formatDate, extractTimestampSeconds } from '../../utils/dates';
import { formatNumber } from '../../utils/numbers';
import { langFlags } from '../../common/langFlags';
import { allFlags } from '../../common/allFlags';
import { getCountryCode } from '../../common/countryNames';
import { getTotalSpent } from '../../utils/money';
import Realities from './UserRealities';
import { convertPrices } from '../../helpers/API/stats';
import CopyToClipboard from '../../components/Common/CopyToClipboard';
import GoToFirebase from '../../components/Common/GoToFirebase';
import { doc, updateDoc, setDoc, increment } from 'firebase/firestore';
import { db } from '../../helpers/firebase';

const exchangeRates = {
  EUR: 1.119,
  USD: 1,
  GBP: 1.251,
  CHF: 1.0956,
  PLN: 0.2486,
  TRY: 0.02943
};

const convertToUSD = (amount, currency) => {
  if (currency === 'USD') return amount;
  const rate = exchangeRates[currency];
  return parseFloat((amount * rate).toFixed(2));
};


function capitalize(text) {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1)?.toLowerCase();
}

function User() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { loading, user, error } = useSelector(state => state.users);
  const [formattedData, setFormattedData] = useState({});
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftSuccess, setGiftSuccess] = useState(false);
  const [removeAvailableRealityLoading, setRemoveAvailableRealityLoading] = useState(false);
  const [removeAvailableRealitySuccess, setRemoveAvailableRealitySuccess] = useState(false);

  useEffect(() => {
    if (!user || !user.id || user.id !== id) {
      dispatch(getUser(id));
    }
  }, [user, id, dispatch]);

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      setFormattedData(formatData(user));
    }
  }, [user]);

  const giftReality = async () => {
    if (!user || !user.id) return;

    setGiftLoading(true);
    setGiftSuccess(false);

    try {
      const userRef = doc(db, 'users', user.id);

      // Use setDoc with merge to handle cases where document or field doesn't exist
      await setDoc(userRef, {
        shop: {
          realitiesAvailable: increment(1)
        }
      }, { merge: true });

      // Update local state to reflect the change immediately
      setFormattedData(prev => ({
        ...prev,
        Shop: {
          ...prev.Shop,
          realitiesAvailable: prev.Shop.realitiesAvailable + 1
        }
      }));

      // Refresh user data
      if (user && user.id) {
        dispatch(getUser(user.id));
      }

      setGiftSuccess(true);

      // Hide success message after 5 seconds
      setTimeout(() => {
        setGiftSuccess(false);
      }, 5000);

    } catch (error) {
      console.error('Error gifting reality:', error);
      alert('Failed to gift reality. Please try again.');
    } finally {
      setGiftLoading(false);
    }
  };

  const removeAvailableReality = async () => {
    if (!user || !user.id) return;

    setRemoveAvailableRealityLoading(true);
    setRemoveAvailableRealitySuccess(false);

    try {
      const userRef = doc(db, 'users', user.id);

      // Use setDoc with merge to handle cases where document or field doesn't exist
      await setDoc(userRef, {
        shop: {
          realitiesAvailable: increment(-1)
        }
      }, { merge: true });

      // Update local state to reflect the change immediately
      setFormattedData(prev => ({
        ...prev,
        Shop: {
          ...prev.Shop,
          realitiesAvailable: prev.Shop.realitiesAvailable - 1
        }
      }));

      // Refresh user data
      if (user && user.id) {
        dispatch(getUser(user.id));
      }

      setRemoveAvailableRealitySuccess(true);

      // Hide success message after 5 seconds
      setTimeout(() => {
        setRemoveAvailableRealitySuccess(false);
      }, 5000);

    } catch (error) {
      console.error('Error removing reality:', error);
      alert('Failed to remove reality. Please try again.');
    } finally {
      setRemoveAvailableRealityLoading(false);
    }
  };

  const formatData = (data) => {
    const totalRealitiesAcquired = data.shop?.totalRealitiesAcquired || 0;
    const itemsAcquired = data.shop?.activity?.itemsAcquired || [];
    const totalSpent = getTotalSpent(itemsAcquired);

    return {
      Information: {
        id: data.id,
        pushToken: data.notifications?.pushTokens && data.notifications.pushTokens.length > 0 ? data.notifications.pushTokens[0] : null,
        email: data.email,
        name: data.name,
        gender: data.gender,
        country: data.country,
        language: data.language,
        voice: data.voice,
        provider: data.provider,
        device: data.device,
        createdAt: data.createdAt.seconds || extractTimestampSeconds(data.createdAt),
        updatedAt: data.updatedAt.seconds || extractTimestampSeconds(data.updatedAt),
        currentReality: data.currentReality,
        marketingConsented: data.emailMarketing ? 'Yes' : 'No',
      },
      Status: {
        onboarded: data.onboarded ? 'Yes' : 'No',
        freeDemoCompleted: data.freeDemoCompleted ? 'Yes' : 'No',
      },
      Shop: {
        activity: { totalRealitiesAcquired, totalSpent, itemCount: itemsAcquired.length },
        items: itemsAcquired,
        realitiesAvailable: data.shop?.realitiesAvailable || 0,
        totalRealitiesAcquired: data.shop?.totalRealitiesAcquired || 0,
        totalRealitiesCreated: data.shop?.totalRealitiesCreated || 0,
        totalUnpaidRealitiesCreated: data.shop?.totalUnpaidRealitiesCreated || 0,
      },
      Tokens: {
        tokensSpent: data.tokensSpent,
      },
      Ads: {
        adsEnabled: data.adsEnabled ? 'Yes' : 'No',
        totalAdsSeen: data.totalAdsSeen,
      },
    };
  };

  useEffect(() => {
    if (error && Object.keys(error).length > 0 && error.message) {
      setTimeout(() => {
        dispatch(clearError());
      }, 5000);
    }
  }, [error, dispatch]);

  const getCurrencyAndAmount = (price) => {
    let currency = 'USD';
    let amount = 0;

    if (typeof price === 'string') {
      if (price.includes('€')) currency = 'EUR';
      else if (price.includes('$')) currency = 'USD';
      else if (price.includes('£')) currency = 'GBP';
      else if (price.includes('₺')) currency = 'TRY';
      else if (price.includes('zł')) currency = 'PLN';

      // Strip the currency symbols to get the amount
      amount = parseFloat(price.replace(/[^0-9.-]+/g,""));
    } else if (typeof price === 'number') {
      amount = price;
    }

    const usdEquivalent = convertToUSD(amount, currency);

    return { currency, amount, usdEquivalent };
  };

  const totalSpent = formattedData.Shop?.activity?.totalSpent || 0;
  const { currency, amount, usdEquivalent } = getCurrencyAndAmount(totalSpent);

  let convertedAmount = '';
  if (currency !== 'USD') {
    const convertedPrices = convertPrices([amount]);
    if (convertedPrices.length > 0) {
      convertedAmount = `- $${formatNumber(convertedPrices[0])}`;
    }
  }

  if (error && Object.keys(error).length > 0) {
    return <div>Error: {error.message}</div>;
  }

  if (loading || !user || !user.id || !Object.keys(formattedData).length) {
    return <Loading />;
  }

  const UserInfo = () => {
    // Calculate shop stats
    const totalSpent = formattedData.Shop?.activity?.totalSpent || 0;
    const { currency, amount: usdEquivalent } = getCurrencyAndAmount(totalSpent);

    return (
      <div className="user-detail-page">
        {/* Top Row: Information and Shop side by side */}
        <div className="top-row">
          <div className="info-section">
            <div className="section-header compact">
              <h3><i className="mdi mdi-account-circle"></i> User Information</h3>
            </div>

            {/* Compact grid layout */}
            <div className="info-compact-grid">
              {/* Row 1: Basic Info */}
              <div className="info-row">
                <div className="info-compact-item">
                  <span className="label">ID:</span>
                  <div className="value">
                    <GoToFirebase userId={formattedData.Information.id} />
                    <span className="compact-text">{formattedData.Information.id}</span>
                    <CopyToClipboard textToCopy={formattedData.Information.id} />
                  </div>
                </div>
              </div>

              {/* Row 2: User Details */}
              <div className="info-row">
                <div className="info-compact-item">
                  <span className="label">Email:</span>
                  <div className="value">
                    <span className="compact-text">{formattedData.Information.email}</span>
                    <CopyToClipboard textToCopy={formattedData.Information.email} />
                  </div>
                </div>
                <div className="info-compact-item">
                  <span className="label">Name:</span>
                  <span className="value">{formattedData.Information.name}</span>
                </div>
              </div>

              {/* Row 3: Location & Language */}
              <div className="info-row">
                <div className="info-compact-item">
                  <span className="label">Country:</span>
                  <div className="value">
                    <img
                      src={allFlags[getCountryCode(formattedData.Information.country)]}
                      alt={formattedData.Information.country}
                      title={formattedData.Information.country}
                      className="flag"
                    />
                    <span>{formattedData.Information.country}</span>
                  </div>
                </div>
                <div className="info-compact-item">
                  <span className="label">Language:</span>
                  <div className="value">
                    <img
                      src={langFlags[formattedData.Information.language]}
                      alt={formattedData.Information.language}
                      title={formattedData.Information.language}
                      className="flag"
                    />
                    <span>{formattedData.Information.language}</span>
                  </div>
                </div>
              </div>

              {/* Row 4: Technical */}
              <div className="info-row">
                <div className="info-compact-item">
                  <span className="label">Device:</span>
                  <div className="value">
                    {formattedData.Information.device === 'android' ? (
                      <i className="mdi mdi-android" style={{ fontSize: '16px', color: '#3DDC84' }} title="Android"></i>
                    ) : formattedData.Information.device === 'ios' ? (
                      <i className="fab fa-apple" style={{ fontSize: '16px', color: '#000' }} title="iOS"></i>
                    ) : (
                      <span>{formattedData.Information.device}</span>
                    )}
                  </div>
                </div>
                <div className="info-compact-item">
                  <span className="label">Provider:</span>
                  <div className="value">
                    {formattedData.Information.provider === 'Google' ? (
                      <i className="fab fa-google" style={{ fontSize: '16px', color: '#DB4437' }} title="Google"></i>
                    ) : formattedData.Information.provider === 'Apple' ? (
                      <i className="fab fa-apple" style={{ fontSize: '16px', color: '#000' }} title="Apple"></i>
                    ) : formattedData.Information.provider === 'Email' ? (
                      <i className="fas fa-envelope" style={{ fontSize: '16px', color: '#007bff' }} title="Email"></i>
                    ) : (
                      <span>{formattedData.Information.provider}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 5: Voice & Preferences */}
              <div className="info-row">
                <div className="info-compact-item">
                  <span className="label">Voice:</span>
                  <span className="value">{formattedData.Information.voice}</span>
                </div>
                <div className="info-compact-item">
                  <span className="label">Marketing:</span>
                  <div className="value">
                    {formattedData.Information.marketingConsented === 'Yes' ? (
                      <div className="consent-badge success">
                        <i className="fas fa-check"></i> Yes
                      </div>
                    ) : (
                      <div className="consent-badge declined">
                        <i className="fas fa-times"></i> No
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 6: Dates */}
              <div className="info-row">
                <div className="info-compact-item">
                  <span className="label">Created:</span>
                  <span className="value date">{formatDate(formattedData.Information.createdAt, true)}</span>
                </div>
                <div className="info-compact-item">
                  <span className="label">Updated:</span>
                  <span className="value date">{formatDate(formattedData.Information.updatedAt, true)}</span>
                </div>
              </div>

              {/* Row 7: Push Token (if available) */}
              {formattedData.Information.pushToken && (
                <div className="info-row">
                  <div className="info-compact-item full-width">
                    <span className="label">Push Token:</span>
                    <div className="value">
                      <span className="compact-text">{formattedData.Information.pushToken}</span>
                      <CopyToClipboard textToCopy={formattedData.Information.pushToken} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="shop-section">
            <div className="section-header">
              <h3><i className="mdi mdi-shopping"></i> Shop & Purchases</h3>
            </div>

            {/* Quick Stats */}
            <div className="shop-stats">
              <div className="stat-card realities">
                <div className="stat-number">{formattedData.Shop.realitiesAvailable}</div>
                <div className="stat-label">Available</div>
              </div>
              <div className="stat-card acquired">
                <div className="stat-number">{formattedData.Shop.activity.totalRealitiesAcquired}</div>
                <div className="stat-label">Acquired</div>
              </div>
              <div className="stat-card created">
                <div className="stat-number">{formattedData.Shop.totalRealitiesCreated}</div>
                <div className="stat-label">Created</div>
              </div>
              <div className="stat-card spent">
                <div className="stat-number">{totalSpent}</div>
                <div className="stat-label">Total Spent</div>
                {currency !== 'USD' && (
                  <div className="stat-usd">${formatNumber(usdEquivalent)}</div>
                )}
              </div>
            </div>

            {/* Additional Shop Details */}
            <div className="shop-details">
              <div className="detail-item">
                <span className="detail-label">Total Unpaid Created:</span>
                <span className="detail-value">{formattedData.Shop.totalUnpaidRealitiesCreated}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Items Count:</span>
                <span className="detail-value">{formattedData.Shop.activity.itemCount}</span>
              </div>
            </div>

            {/* Recent Purchases */}
            {formattedData.Shop.items && formattedData.Shop.items.length > 0 && (
              <div className="recent-purchases">
                <h4><i className="mdi mdi-receipt"></i> Recent Purchases</h4>
                <div className="purchases-list">
                  {formattedData.Shop.items.slice(0, 3).map((item, index) => {
                    const createdAt = new Date(item.createdAt.seconds * 1000);
                    const now = new Date();
                    const timeDifferenceInMs = now - createdAt;
                    const timeAgoInDays = Math.floor(timeDifferenceInMs / (1000 * 60 * 60 * 24));

                    let timeAgo;
                    if (timeAgoInDays === 0) {
                      const timeAgoInHours = Math.floor(timeDifferenceInMs / (1000 * 60 * 60));
                      timeAgo = `${timeAgoInHours}h ago`;
                    } else {
                      timeAgo = `${timeAgoInDays}d ago`;
                    }

                    return (
                      <div key={index} className="purchase-item">
                        <div className="purchase-product">{item.product.identifier}</div>
                        <div className="purchase-time">{timeAgo}</div>
                      </div>
                    );
                  })}
                  {formattedData.Shop.items.length > 3 && (
                    <div className="more-items">
                      +{formattedData.Shop.items.length - 3} more items
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="shop-actions">
              <button
                onClick={giftReality}
                disabled={giftLoading || giftSuccess}
                className={`action-btn gift-btn ${giftSuccess ? 'success' : ''}`}
              >
                {giftLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Gifting...
                  </>
                ) : giftSuccess ? (
                  <>
                    <i className="fas fa-check-circle"></i>
                    Gifted!
                  </>
                ) : (
                  <>
                    <i className="fas fa-gift"></i>
                    Gift Reality
                  </>
                )}
              </button>

              {formattedData.Shop.realitiesAvailable > 1 && (
                <button
                  onClick={removeAvailableReality}
                  disabled={removeAvailableRealityLoading || removeAvailableRealitySuccess}
                  className={`action-btn remove-btn ${removeAvailableRealitySuccess ? 'success' : ''}`}
                >
                  {removeAvailableRealityLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Removing...
                    </>
                  ) : removeAvailableRealitySuccess ? (
                    <>
                      <i className="fas fa-check-circle"></i>
                      Removed!
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash"></i>
                      Remove Reality
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Row: Realities taking full width */}
        <div className="bottom-row">
          <div className="realities-section">
            <Realities user={user} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-content">
      <UserInfo />
    </div>
  );
}

export default User;

